import { useEffect, useState, useRef, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useAuthStore } from '@/stores/authStore';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import { usePreferencesStore } from '@/stores/preferencesStore';
import { usePostsStore } from '@/stores/postsStore';
import { Loader2, Check } from 'lucide-react';

// Dev mode configuration - single source of truth
import { DEV_MODE, MOCK_USER } from '@/lib/devMode';
// Dev-mode mock-auth harness (Phase 4c) — dead code in production (DEV_MODE gated)
import { seedDevData, installTourDevApi, installDevApi } from '@/lib/devSeed';

// Components
import TrialExpiredModal from '@/components/TrialExpiredModal';
import TrialBanner from '@/components/TrialBanner';
import TourProvider from '@/components/tour/TourProvider';
import WelcomeModal from '@/components/tour/WelcomeModal';
import CloseConfirmModal from '@/components/tour/CloseConfirmModal';
import CheckpointModal from '@/components/tour/CheckpointModal';
import EndScreen from '@/components/tour/EndScreen';

// Critical pages - loaded immediately (auth flow)
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import Terms from '@/pages/Terms';
import Privacy from '@/pages/Privacy';
import ConsentGate from '@/components/ConsentGate';

// Lazy-loaded pages - loaded on demand for better initial bundle size
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const VerifyEmail = lazy(() => import('@/pages/VerifyEmail'));
const AuthCallback = lazy(() => import('@/pages/AuthCallback'));
const Onboarding = lazy(() => import('@/pages/Onboarding'));
const Tool = lazy(() => import('@/pages/Tool'));
const Feed = lazy(() => import('@/pages/Feed'));
const MyPosts = lazy(() => import('@/pages/MyPosts'));
const Collections = lazy(() => import('@/pages/Collections'));
const Analytics = lazy(() => import('@/pages/Analytics'));
const TrialPending = lazy(() => import('@/pages/TrialPending'));
const TrialRejected = lazy(() => import('@/pages/TrialRejected'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const CaseStudy = lazy(() => import('@/pages/CaseStudy'));

// Admin Pages - lazy loaded
const AdminGuard = lazy(() => import('@/pages/admin/AdminGuard'));
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const TrialRequests = lazy(() => import('@/pages/admin/TrialRequests'));
const RequestDetail = lazy(() => import('@/pages/admin/RequestDetail'));

// Settings Pages - lazy loaded
const Profile = lazy(() => import('@/pages/settings/Profile'));
const Preferences = lazy(() => import('@/pages/settings/Preferences'));
const Subscription = lazy(() => import('@/pages/settings/Subscription'));
const Connections = lazy(() => import('@/pages/settings/Connections'));
const Workspaces = lazy(() => import('@/pages/settings/Workspaces'));

// Global indicator shown on all pages while a generation is in progress
function GeneratingBanner() {
  const isGenerating = usePostsStore((s) => s.isGenerating);
  const location = useLocation();
  const navigate = useNavigate();
  const [showComplete, setShowComplete] = useState(false);
  const prevRef = useRef(false);

  useEffect(() => {
    if (prevRef.current && !isGenerating) {
      // Generation just finished — show "view results" toast if not on /tool
      if (location.pathname !== '/tool') {
        setShowComplete(true);
        const t = setTimeout(() => setShowComplete(false), 6000);
        return () => clearTimeout(t);
      }
    }
    prevRef.current = isGenerating;
  }, [isGenerating, location.pathname]);

  if (!isGenerating && !showComplete) return null;

  return (
    <div className="fixed bottom-4 right-4 z-[200] flex flex-col items-end gap-2 pointer-events-none">
      {isGenerating && (
        <div className="bg-ink text-white text-sm font-semibold px-4 py-2.5 rounded-full shadow-lg flex items-center gap-2 pointer-events-auto">
          <Loader2 className="w-4 h-4 animate-spin text-cargo" />
          Generating…
        </div>
      )}
      {showComplete && (
        <button
          onClick={() => { navigate('/tool'); setShowComplete(false); }}
          className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-4 py-2.5 rounded-full shadow-lg flex items-center gap-2 transition-colors pointer-events-auto"
        >
          <Check className="w-4 h-4" />
          Generation complete — view results
        </button>
      )}
    </div>
  );
}

// Loading component
function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-dark-bg">
      <div className="text-center">
        <h1 
          className="font-bold text-brand-dark dark:text-dark-text text-4xl mb-4 whitespace-nowrap"
          style={{
            fontFamily: 'Futura, "Century Gothic", "Apple SD Gothic Neo", sans-serif',
            letterSpacing: '-0.02em',
          }}
        >
          Zupa
        </h1>
        <div className="flex items-center justify-center gap-1">
          <span className="text-brand-dark dark:text-dark-text thinking-dots">
            Loading<span>.</span><span>.</span><span>.</span>
          </span>
        </div>
      </div>
    </div>
  );
}

// Auth guard component with trial expiration check and onboarding redirect
function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, user, onboardingStatus, fetchCurrentUser } = useAuthStore();
  const [showTrialExpired, setShowTrialExpired] = useState(false);
  const [hasCheckedAll, setHasCheckedAll] = useState(false);
  const location = useLocation();

  // Check if trial has expired and user needs to choose a plan
  useEffect(() => {
    if (DEV_MODE) {
      setHasCheckedAll(true);
      return;
    }

    // Auth check finished but no user — allow redirect to login
    if (!isLoading && !user) {
      setHasCheckedAll(true);
      return;
    }

    if (user && !isLoading && onboardingStatus !== null) {
      // Admins ALWAYS bypass paywall - they never see trial expired modal
      if (user.is_admin) {
        setShowTrialExpired(false);
        setHasCheckedAll(true);
        return;
      }

      // Show modal when trial_ended is true (trial expired, no paid subscription)
      // This means user must choose a plan before accessing the app
      const trialExpiredNeedsPlan = user.trial_ended === true;

      setShowTrialExpired(trialExpiredNeedsPlan);
      setHasCheckedAll(true);
    }
  }, [user, isLoading, onboardingStatus]);

  // Handle when user selects a plan
  const handlePlanSelected = async (addCompanyIntelligence?: boolean) => {
    if (addCompanyIntelligence) {
      // Navigate to workspaces to set up company intelligence
      window.location.href = '/settings/workspaces?action=create';
      return;
    }

    // Refresh all data in-place so Tool page has fresh state when modal closes.
    // Avoids a full page reload which can leave activeWorkspace null (causing blank Tool).
    try {
      await Promise.all([
        fetchCurrentUser(),
        useWorkspaceStore.getState().fetchWorkspaces(),
        useWorkspaceStore.getState().fetchActiveWorkspace(),
        useWorkspaceStore.getState().fetchLimits(),
      ]);
    } catch {
      // Fallback to reload if refresh fails
      window.location.reload();
      return;
    }

    setShowTrialExpired(false);
  };

  // Bypass auth in dev mode
  if (DEV_MODE) {
    return <>{children}</>;
  }

  if (isLoading || !hasCheckedAll) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Show trial expired modal (user cannot proceed until they choose a plan)
  if (showTrialExpired) {
    return (
      <>
        <TrialExpiredModal 
          isOpen={true} 
          onPlanSelected={handlePlanSelected}
        />
        <div className="min-h-screen bg-gray-100 dark:bg-dark-bg" />
      </>
    );
  }

  // Check if Pro user needs onboarding (not on onboarding page already)
  if (
    user?.is_pro && 
    onboardingStatus && 
    !onboardingStatus.is_onboarded && 
    location.pathname !== '/onboarding'
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

// Public route - redirect based on auth and onboarding status
// IMPORTANT: Does not depend on isLoading from authStore to prevent
// unmounting Login/Register components during form submissions
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user, onboardingStatus } = useAuthStore();
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);

  // Check auth status once on mount
  useEffect(() => {
    // Give a brief moment for the auth store to initialize
    const checkAuth = async () => {
      // Wait for initial auth check
      await new Promise(resolve => setTimeout(resolve, 50));
      setHasCheckedAuth(true);
    };
    checkAuth();
  }, []);

  // In dev mode, don't redirect - let user access public pages
  if (DEV_MODE) {
    return <>{children}</>;
  }

  // Show loading only during initial auth check, not during login/register form submissions
  // Once hasCheckedAuth is true, we never show loading screen again
  if (!hasCheckedAuth) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    // Pro users who haven't onboarded go to onboarding
    if (user?.is_pro && onboardingStatus && !onboardingStatus.is_onboarded) {
      return <Navigate to="/onboarding" replace />;
    }
    // All other authenticated users go to the feed
    return <Navigate to="/feed" replace />;
  }

  return <>{children}</>;
}

function App() {
  const { fetchCurrentUser, setUser, setLoading } = useAuthStore();
  const { fetchWorkspaces, fetchActiveWorkspace, fetchLimits } = useWorkspaceStore();
  const { fetchPreferences } = usePreferencesStore();

  // Auto-reload after long inactivity to prevent blank screen
  useEffect(() => {
    let hiddenAt: number | null = null;
    const IDLE_THRESHOLD = 30 * 60 * 1000; // 30 minutes

    const handleVisibilityChange = () => {
      if (document.hidden) {
        hiddenAt = Date.now();
      } else if (hiddenAt && Date.now() - hiddenAt > IDLE_THRESHOLD) {
        window.location.reload();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useEffect(() => {
    if (DEV_MODE) {
      // Phase 4c: seed the dev_* localStorage keys (workspace, services, voice,
      // personas, preferences, and the content the app used to be empty of)
      // BEFORE the fetches below read them, so the tour has rich state to run
      // against. Then expose window.__zupaTourDev and window.__zupaDev.
      seedDevData();
      installTourDevApi();
      installDevApi();
      // Set mock data in dev mode
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setUser(MOCK_USER as any);
      setLoading(false);
      // Also fetch workspaces and preferences in dev mode
      fetchWorkspaces();
      fetchActiveWorkspace();
      fetchLimits();
      fetchPreferences();
    } else {
      fetchCurrentUser().then(() => {
        // Only fetch workspaces and preferences if user is authenticated
        const token = localStorage.getItem('access_token');
        if (token) {
          fetchWorkspaces();
          fetchActiveWorkspace();
          fetchLimits();
          fetchPreferences();
        }
      });
    }
  }, [fetchCurrentUser, setUser, setLoading, fetchWorkspaces, fetchActiveWorkspace, fetchLimits, fetchPreferences]);

  return (
    <TooltipProvider delayDuration={400}>
    <BrowserRouter>
      {DEV_MODE && (
        <div className="fixed top-0 left-0 right-0 bg-yellow-400 text-yellow-900 text-center py-1 text-sm font-medium z-[100]">
          DEV MODE - Authentication bypassed. Set DEV_MODE = false in lib/devMode.ts for production.
        </div>
      )}
      {/* Trial warning banner for authenticated users */}
      <TrialBanner />
      {/* Global generating indicator — shown on all pages during active generation */}
      <GeneratingBanner />
      <ConsentGate>
      {/* Guided product tour — driver.js spotlight host + tour modals */}
      <TourProvider />
      <WelcomeModal />
      <CloseConfirmModal />
      <CheckpointModal />
      <EndScreen />
      <div className={DEV_MODE ? 'pt-8' : ''}>
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            {/* Public routes */}
            <Route
              path="/"
              element={
                <PublicRoute>
                  <Landing />
                </PublicRoute>
              }
            />
            {/* /home is the marketing page, reachable whether or not you are logged in —
                it is the one route an authenticated user can use to go read the public site
                (pricing, features) without logging out. Deliberately NOT wrapped in
                PublicRoute: that gate would bounce an authenticated visitor to /tool and
                make the site unreachable from inside the app.
                skipAuthRedirect keeps the page from redirecting itself; Landing then renders
                the Navbar with isHomePage, which swaps the CTA for "Back to App" and points
                the logo at /tool — so you are never stranded and never look logged out. */}
            <Route path="/home" element={<Landing skipAuthRedirect />} />
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              }
            />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <ForgotPassword />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <PublicRoute>
                  <ResetPassword />
                </PublicRoute>
              }
            />
            <Route
              path="/verify-email"
              element={<VerifyEmail />}
            />
            <Route
              path="/auth/google/callback"
              element={<AuthCallback />}
            />

            {/* Protected routes */}
            <Route
              path="/onboarding"
              element={
                <PrivateRoute>
                  <Onboarding />
                </PrivateRoute>
              }
            />
            <Route
              path="/tool"
              element={
                <PrivateRoute>
                  <Tool />
                </PrivateRoute>
              }
            />
            <Route
              path="/feed"
              element={
                <PrivateRoute>
                  <Feed />
                </PrivateRoute>
              }
            />

            {/* Case Studies */}
            <Route
              path="/case-studies"
              element={
                <PrivateRoute>
                  <CaseStudy />
                </PrivateRoute>
              }
            />

            {/* Dashboard */}
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />

            {/* Posts & Collections */}
            <Route
              path="/posts"
              element={
                <PrivateRoute>
                  <MyPosts />
                </PrivateRoute>
              }
            />
            <Route
              path="/collections"
              element={
                <PrivateRoute>
                  <Collections />
                </PrivateRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <PrivateRoute>
                  <Analytics />
                </PrivateRoute>
              }
            />

            {/* Settings Routes */}
            <Route
              path="/settings"
              element={<Navigate to="/settings/profile" replace />}
            />
            <Route
              path="/settings/profile"
              element={
                <PrivateRoute>
                  <Profile />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings/subscription"
              element={
                <PrivateRoute>
                  <Subscription />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings/workspaces"
              element={
                <PrivateRoute>
                  <Workspaces />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings/preferences"
              element={
                <PrivateRoute>
                  <Preferences />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings/connections"
              element={
                <PrivateRoute>
                  <Connections />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings/connections/callback/:platform"
              element={
                <PrivateRoute>
                  <Connections />
                </PrivateRoute>
              }
            />

            {/* Legacy routes - redirect to workspaces */}
            <Route
              path="/settings/company"
              element={<Navigate to="/settings/workspaces" replace />}
            />
            <Route
              path="/settings/services"
              element={<Navigate to="/settings/workspaces" replace />}
            />

            {/* Trial Status Pages */}
            <Route
              path="/trial-pending"
              element={<TrialPending />}
            />
            <Route
              path="/trial-rejected"
              element={<TrialRejected />}
            />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <Suspense fallback={<LoadingScreen />}>
                  <AdminGuard>
                    <TrialRequests />
                  </AdminGuard>
                </Suspense>
              }
            />
            <Route
              path="/admin/dashboard"
              element={
                <Suspense fallback={<LoadingScreen />}>
                  <AdminGuard>
                    <AdminDashboard />
                  </AdminGuard>
                </Suspense>
              }
            />
            <Route
              path="/admin/requests/:userId"
              element={
                <Suspense fallback={<LoadingScreen />}>
                  <AdminGuard>
                    <RequestDetail />
                  </AdminGuard>
                </Suspense>
              }
            />

            {/* 404 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </div>
      </ConsentGate>
    </BrowserRouter>
    </TooltipProvider>
  );
}

export default App;
